import {HeaderState} from './HeaderState'
import type {HeaderState_issue$key} from './__generated__/HeaderState_issue.graphql'
import {Box, Label, Link, Text} from '@primer/react'
import {userHovercardPath} from '@github-ui/paths'
import {LABELS} from '../../constants'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {TaskListStatus} from '../shared/TaskListStatus'
import type {TaskListStatus$key} from '../shared/__generated__/TaskListStatus.graphql'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {HeaderTitle, type HeaderTitleProps} from './HeaderTitle'
import type {TitleProps} from '@primer/react/experimental'
import {HeaderMenu, type HeaderMenuProps} from './HeaderMenu'
import {useMemo} from 'react'
import {ContentWrapper} from '../ContentWrapper'
import {HeaderIssueType} from './HeaderIssueType'
import type {Header$data} from './__generated__/Header.graphql'

type Props = {
  stateData: HeaderState_issue$key
  taskListStatusData?: TaskListStatus$key
  showRepositoryPill?: boolean
  repoId: string
  repoOwner: string
  repoName: string
  repository: {isPrivate: boolean; ownerAvatarUrl: string; ownerType: string}
  commentsCount: number
  titleAs?: TitleProps['as']
  innerSx?: BetterSystemStyleObject
  isSticky: boolean
  stickyStyles: BetterSystemStyleObject
  headerMenuProps: HeaderMenuProps
  headerTitleProps: HeaderTitleProps
  typeData: Header$data['issueType']
}

export function HeaderMetadata({
  stateData,
  taskListStatusData,
  showRepositoryPill,
  repoId,
  repoOwner,
  repoName,
  repository,
  commentsCount,
  titleAs,
  innerSx,
  isSticky,
  stickyStyles,
  headerMenuProps,
  headerTitleProps,
  typeData,
}: Props) {
  const styles = useMemo(() => {
    const sx: BetterSystemStyleObject = {
      display: 'flex',
      gap: '8px',
      flexFlow: 'row wrap',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '56px',
      borderBottom: '1px solid',
      borderColor: isSticky ? 'border.default' : 'transparent',
      marginBottom: 3,
      ...stickyStyles,
      transitionDuration: '.1s',
    }

    return sx
  }, [isSticky, stickyStyles])

  return (
    <Box sx={{...styles}} className={'js-sticky is-stuck js-position-sticky'}>
      <ContentWrapper sx={{display: 'flex', alignItems: 'center', gap: 2, ...innerSx}}>
        <HeaderState stateData={stateData} />
        {typeData && <HeaderIssueType name={typeData.name} />}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {isSticky && (
            <Box as={titleAs} sx={{display: 'flex'}}>
              <HeaderTitle {...headerTitleProps} repoName={repoName} repoOwner={repoOwner} />
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '8px',
              flexWrap: isSticky ? 'nowrap' : 'wrap',
              whiteSpace: isSticky ? 'nowrap' : 'normal',
            }}
          >
            {showRepositoryPill && (
              <>
                <Box
                  sx={{
                    color: 'fg.subtle',
                    fontWeight: 'normal',
                    fontSize: 0,
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                  }}
                >
                  <Link
                    href={`/${repoOwner}`}
                    sx={{color: 'fg.muted', fontWeight: 'normal'}}
                    data-hovercard-url={userHovercardPath({owner: repoOwner})}
                  >
                    <GitHubAvatar
                      square={repository.ownerType === LABELS.organization}
                      src={repository.ownerAvatarUrl}
                      size={16}
                      alt={`${repoOwner}`}
                      sx={{mr: 1}}
                    />
                    {repoOwner}
                  </Link>
                  {'/'}
                  <Link href={`/${repoOwner}/${repoName}`} sx={{color: 'fg.muted', fontWeight: 'normal'}}>
                    {repoName}
                  </Link>
                  <Label data-watch-overflow sx={{ml: 1, color: 'fg.default'}}>
                    {repository.isPrivate ? LABELS.private : LABELS.public}
                  </Label>
                </Box>
              </>
            )}
            {taskListStatusData && <TaskListStatus taskListStatus={taskListStatusData} />}
            <Box data-watch-overflow sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px'}}>
              {showRepositoryPill && <Box sx={{color: 'fg.muted'}}>·</Box>}
              <Text sx={{color: 'fg.muted', fontSize: 0}}>{commentsCount} comments</Text>
            </Box>
          </Box>
        </Box>
        {isSticky && (
          <Box>
            <HeaderMenu
              {...headerMenuProps}
              showCreateIssueButton={false}
              scopedRepository={{id: repoId, name: repoName, owner: repoOwner}}
            />
          </Box>
        )}
        {headerMenuProps.metadataPaneTrigger}
      </ContentWrapper>
    </Box>
  )
}

try{ HeaderMetadata.displayName ||= 'HeaderMetadata' } catch {}